import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    TextField,
    Typography,
} from '@mui/material';
import { IAccount } from '../Interfaces/IAccount';
import { IHutBookEntry, IHutBookEntryInput, IHutBookRoom, hutBookEntryInputSchema } from '../Interfaces/IHutBook';
import { useAlert } from '../core/AlertContext';
import { useCreateHutBookEntry, useGetHutBookRooms, useUpdateHutBookEntry } from '../hooks/hutBook';
import { useGetUsers } from '../hooks/users';
import { getCurrentUserId } from '../utils/permission';
import { fieldSx, primaryButtonSx, secondaryButtonSx } from '../core/pvtaStyles';

interface HutBookEntryEditProps {
    initialEntry?: IHutBookEntry | null;
    onClose: () => void;
    showHeading?: boolean;
}

const getUserLabel = (user: IAccount): string => {
    const fullName = `${user.first_name} ${user.last_name}`.trim();
    return fullName || user.email || user.username;
};

const HutBookEntryEdit: React.FC<HutBookEntryEditProps> = ({ initialEntry = null, onClose, showHeading = true }) => {
    const currentUserId = getCurrentUserId();
    const isCreateMode = initialEntry === null;
    const [createForOtherPerson, setCreateForOtherPerson] = useState<boolean>(
        initialEntry ? initialEntry.entry_for.id !== currentUserId : false,
    );
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [formValues, setFormValues] = useState<IHutBookEntryInput>({
        room_id: initialEntry?.room.id ?? 0,
        date_from: initialEntry?.date_from ?? '',
        date_to: initialEntry?.date_to ?? '',
        entry_for_id: initialEntry?.entry_for.id ?? currentUserId,
    });
    const { hutBookRoomArray, isLoadingHutBookRoomArray } = useGetHutBookRooms();
    const { userArray, isLoadingUserArray } = useGetUsers();
    const { createHutBookEntry, isCreatingHutBookEntry } = useCreateHutBookEntry();
    const { updateHutBookEntry, isUpdatingHutBookEntry } = useUpdateHutBookEntry();
    const { showAlert } = useAlert();
    const isSaving = isCreatingHutBookEntry || isUpdatingHutBookEntry;

    useEffect(() => {
        if (!createForOtherPerson) {
            setFormValues((prev) => ({
                ...prev,
                entry_for_id: currentUserId,
            }));
        }
    }, [createForOtherPerson, currentUserId]);

    const handleSubmit = async () => {
        try {
            const payload = hutBookEntryInputSchema.parse(formValues);
            if (isCreateMode) {
                await createHutBookEntry(payload);
                showAlert({ text: 'Hüttenbuch-Eintrag erfolgreich angelegt.' });
            } else if (initialEntry) {
                await updateHutBookEntry({ id: initialEntry.id, ...payload });
                showAlert({ text: 'Hüttenbuch-Eintrag erfolgreich aktualisiert.' });
            }
            setConfirmOpen(false);
            onClose();
        } catch {
            showAlert({
                text: isCreateMode ? 'Fehler beim Anlegen des Hüttenbuch-Eintrags.' : 'Fehler beim Aktualisieren des Hüttenbuch-Eintrags.',
                severity: 'error',
            });
        }
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isCreateMode) {
            setConfirmOpen(true);
            return;
        }
        void handleSubmit();
    };

    const selectedRoom = hutBookRoomArray.find((room) => room.id === formValues.room_id) ?? null;
    const availableUsers = (userArray ?? []).filter((user) => !user.verstorben);
    const selectedUser = availableUsers.find((user) => user.id === formValues.entry_for_id) ?? null;

    return (
        <>
            <Box sx={{ minWidth: 0 }}>
                <form onSubmit={handleFormSubmit}>
                    {showHeading && (
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Eintrag erfassen
                        </Typography>
                    )}
                    <Autocomplete
                        options={hutBookRoomArray}
                        value={selectedRoom}
                        onChange={(_, newValue: IHutBookRoom | null) => {
                            setFormValues((prev) => ({
                                ...prev,
                                room_id: newValue?.id ?? 0,
                            }));
                        }}
                        getOptionLabel={(option) => option.name}
                        loading={isLoadingHutBookRoomArray}
                        sx={{ ...fieldSx }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Zimmer"
                                margin="normal"
                                size="small"
                                fullWidth
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoadingHutBookRoomArray ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <TextField
                        label="Von"
                        type="date"
                        value={formValues.date_from}
                        onChange={(event) => {
                            setFormValues((prev) => ({
                                ...prev,
                                date_from: event.target.value,
                            }));
                        }}
                        fullWidth
                        margin="normal"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...fieldSx }}
                    />
                    <TextField
                        label="Bis"
                        type="date"
                        value={formValues.date_to}
                        onChange={(event) => {
                            setFormValues((prev) => ({
                                ...prev,
                                date_to: event.target.value,
                            }));
                        }}
                        fullWidth
                        margin="normal"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...fieldSx }}
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={createForOtherPerson}
                                onChange={(event) => setCreateForOtherPerson(event.target.checked)}
                            />
                        )}
                        label="Für eine andere Person erstellen"
                    />
                    {createForOtherPerson ? (
                        <Autocomplete
                            options={availableUsers}
                            value={selectedUser}
                            onChange={(_, newValue: IAccount | null) => {
                                setFormValues((prev) => ({
                                    ...prev,
                                    entry_for_id: newValue?.id ?? 0,
                                }));
                            }}
                            getOptionLabel={getUserLabel}
                            loading={isLoadingUserArray}
                            sx={{ ...fieldSx }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Person"
                                    margin="normal"
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoadingUserArray ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    ) : null}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
                        <Button onClick={onClose} sx={{ ...secondaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                            Schließen
                        </Button>
                        <Button type="submit" disabled={isSaving} sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                            {isSaving ? 'Speichern...' : 'Speichern'}
                        </Button>
                    </Box>
                </form>
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Eintrag endgültig speichern?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Sind Sie sicher, dass Sie diesen Eintrag speichern möchten? Danach kann er nicht mehr von Ihnen geändert werden.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmOpen(false)} sx={{ ...secondaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                        Abbrechen
                    </Button>
                    <Button onClick={() => { void handleSubmit(); }} disabled={isSaving} sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                        Bestätigen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default HutBookEntryEdit;
